import React, { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { useWidgetProps } from "../use-widget-props";
import { useOpenAiGlobal } from "../use-openai-global";
import { useDevMode } from "../use-dev-mode";
import { History as HistoryIcon } from "lucide-react";
import HistoryItem from "./HistoryItem";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import LoadingSkeleton from "./LoadingSkeleton";

function App() {
  // Detect if we're in development mode using centralized configuration
  const { isDev } = useDevMode();

  // Mock data for development
  const mockData = [
    {
      humanization_id: "h1-mock-001",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date().toISOString(),
    },
    {
      humanization_id: "h2-mock-002",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date(Date.now() - 120000).toISOString(), // 2 mins ago
    },
    {
      humanization_id: "h3-mock-003",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date(Date.now() - 14400000).toISOString(), // 4 hours ago
    },
    {
      humanization_id: "h4-mock-004",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date(Date.now() - 46800000).toISOString(), // 13 hours ago
    },
    {
      humanization_id: "h5-mock-005",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date().toISOString(),
    },
    {
      humanization_id: "h6-mock-006",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date().toISOString(),
    },
    {
      humanization_id: "h7-mock-007",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date(Date.now() - 120000).toISOString(), // 2 mins ago
    },
    {
      humanization_id: "h8-mock-008",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date(Date.now() - 14400000).toISOString(), // 4 hours ago
    },
    {
      humanization_id: "h9-mock-009",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date(Date.now() - 46800000).toISOString(), // 13 hours ago
    },
    {
      humanization_id: "h10-mock-010",
      humanized_text:
        "Airplane turbulence, often described as a sudden, unpredictable movement of an aircraft, is a common occurrence during flights.",
      original_text: "Airplane turbulence is a sudden movement.",
      human_score: 1.0,
      ai_score: 0.0,
      created_at: new Date().toISOString(),
    },
  ];

  const props = useWidgetProps({
    humanisations: [],
    total_pages: 0,
    current_page: 0,
    total_humanizations: 0,
  });

  const theme = useOpenAiGlobal("theme") || "light";
  const [initialLoading, setInitialLoading] = useState(isDev);
  const [mockHumanisations, setMockHumanisations] = useState(
    isDev ? mockData : []
  );
  const [loadingId, setLoadingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use mock data in dev mode, otherwise use props
  const historyItems = isDev ? mockHumanisations : props.humanisations || [];
  const displayedItems = historyItems.slice(0, 5);
  const totalHumanizations = isDev
    ? mockHumanisations.length
    : props.total_humanizations;
  const remainingCount = Math.max(0, totalHumanizations - 5);

  const isLoading = isDev
    ? false
    : props.total_humanizations === 0 && props.current_page === 0;
  const isDark = theme === "dark";

  // Initial loading simulation (2 seconds)
  useEffect(() => {
    if (isDev && initialLoading) {
      const timer = setTimeout(() => {
        setInitialLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isDev, initialLoading]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "just now";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  };

  const handleItemClick = async (humanization_id) => {
    if (!humanization_id || openMenuId) return;

    try {
      setLoadingId(humanization_id);
      await window.openai.sendFollowUpMessage({
        prompt: `Show me the humanization with ID: ${humanization_id}`,
      });
    } catch (error) {
      console.error("Failed to load humanization:", error);
    } finally {
      setLoadingId(null);
    }
  };

  const handleViewFullHistory = () => {
    window.openai.openExternal({
      href: "https://detect.ai/humanize",
    });
  };

  const handleMenuClick = (e, humanization_id) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === humanization_id ? null : humanization_id);
  };

  const handleDeleteClick = (humanization_id) => {
    setDeleteTargetId(humanization_id);
    setShowDeleteModal(true);
    setOpenMenuId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;

    setIsDeleting(true);

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Remove the item from mock data in dev mode
    if (isDev) {
      setMockHumanisations((prev) =>
        prev.filter((item) => item.humanization_id !== deleteTargetId)
      );
    } else {
      try {
        await window.openai.sendFollowUpMessage({
          prompt: `Delete the humanization with ID: ${deleteTargetId}`,
        });
      } catch (error) {
        console.error("Failed to delete:", error);
        setIsDeleting(false);
        setShowDeleteModal(false);
        setDeleteTargetId(null);
        return;
      }
    }

    setIsDeleting(false);
    setShowDeleteModal(false);
    setDeleteTargetId(null);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };
    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuId]);

  // Show loading skeleton on initial load (dev mode) or when actually loading
  if (initialLoading || isLoading) {
    return <LoadingSkeleton isDark={isDark} />;
  }

  return (
    <>
      <div
        className={`antialiased w-full overflow-hidden rounded-3xl ${
          isDark
            ? "bg-[#212121] border border-white-alpha-20"
            : "bg-white border border-black-alpha-15"
        }`}
        style={{ boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.05)" }}
      >
        {/* Header */}
        <div className={`flex flex-row items-center gap-8 p-16`}>
          <div
            className="flex items-center justify-center rounded-full p-8"
            style={{
              background:
                "radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.26) 0%, rgba(255, 255, 255, 0) 100%), #4D47DC",
            }}
          >
            <HistoryIcon
              className="w-[13px] h-[13px]"
              style={{ color: "#FFFFFF" }}
              strokeWidth={2}
            />
          </div>
          <div className="flex-1">
            <div
              className={`font-medium text-[17px] leading-[1.35em] tracking-[-2.53%] ${
                isDark ? "text-white" : "text-black"
              }`}
            >
              Humanization History
            </div>
          </div>
          {historyItems.length > 0 && (
            <div
              className={`flex items-center justify-center p-6 rounded-sm border border-solid transition-all duration-300 ${
                isDark ? "border-white-alpha-05" : "border-black-alpha-05"
              } ${isDark ? "bg-[#000000]" : "bg-[#F8F9FB]"}`}
            >
              <span
                className={`font-medium text-[12px] leading-[0.7em] tracking-[-2%] transition-all duration-300 ${
                  isDark ? "text-[#CDCDCD]" : "text-[#8F8F8F]"
                }`}
                key={totalHumanizations}
              >
                {totalHumanizations}
              </span>
            </div>
          )}
        </div>

        {/* History List */}
        <div>
          {historyItems.length === 0 ? (
            <div className="px-4 py-12 text-center flex flex-col items-center gap-8">
              <div className="flex justify-center">
                <div
                  className={`flex items-center justify-center rounded-full p-16 border border-solid ${
                    isDark ? "border-white-alpha-05" : "border-black-alpha-05"
                  } ${isDark ? "bg-[#2F3039] " : "bg-[#F8F9FB]"}`}
                >
                  <HistoryIcon
                    className="w-32 h-32"
                    style={{ color: isDark ? "#CDCDCD" : "#8F8F8F" }}
                    strokeWidth={2}
                  />
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div
                  className={`font-medium text-[17px] leading-[1.41em] tracking-[-1.29%] ${
                    isDark ? "text-[#CDCDCD]" : "text-[#5D5D5D]"
                  }`}
                >
                  No history yet
                </div>
                <div
                  className={`font-regular text-[14px] leading-[1.43em] tracking-[-1.29%] ${
                    isDark ? "text-[#b4b3b3]" : "text-[#a1a1a9]"
                  }`}
                >
                  Your humanized texts will appear here
                </div>
              </div>
            </div>
          ) : (
            <>
              {displayedItems.map((item, index) => (
                <HistoryItem
                  key={item.humanization_id || index}
                  item={item}
                  isLoading={loadingId === item.humanization_id}
                  onClick={() => handleItemClick(item.humanization_id)}
                  onMenuClick={(e) => handleMenuClick(e, item.humanization_id)}
                  onDelete={() => handleDeleteClick(item.humanization_id)}
                  formatTimestamp={formatTimestamp}
                  isMenuOpen={openMenuId === item.humanization_id}
                  isDark={isDark}
                  index={index}
                />
              ))}

              <div
                className={`flex items-center justify-between pl-16 pr-8 py-8`}
              >
                {remainingCount > 0 && (
                  <button
                    onClick={handleViewFullHistory}
                    className="flex items-center gap-1"
                    style={{ gap: "4px" }}
                  >
                    <span
                      className={`font-regular text-[17px] leading-[1.41em] tracking-[-1.29%] ${
                        isDark ? "text-[#CDCDCD]" : "text-[#5D5D5D]"
                      }`}
                    >
                      + {remainingCount} more
                    </span>
                  </button>
                )}
                <button
                  onClick={handleViewFullHistory}
                  className="flex items-center justify-center gap-3 py-8 px-8"
                >
                  <span
                    className={`font-medium text-[14px] leading-[1.43em] tracking-[-1.29%] underline underline-offset-2 transition-colors ${
                      isDark
                        ? "text-[#CDCDCD] hover:text-white-alpha-90 active:text-white-alpha-80"
                        : "text-[#5D5D5D] hover:text-black-alpha-80 active:text-black-alpha-90"
                    }`}
                  >
                    See all in Detect AI
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showDeleteModal && deleteTargetId && (
        <DeleteConfirmationModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            if (!isDeleting) {
              setShowDeleteModal(false);
              setDeleteTargetId(null);
            }
          }}
          isDeleting={isDeleting}
          isDark={isDark}
        />
      )}
    </>
  );
}

// Mount the app
const rootElement = document.getElementById("humanizer-history-root");
if (rootElement) {
  createRoot(rootElement).render(<App />);
}

export default App;
